import React, { forwardRef } from 'react';
import { Button, type ButtonProps, Typography, useTheme, alpha, darken } from '@mui/material';
import { smoothColorTransform } from '../../theme/transitions';

export interface EliceButtonProps extends ButtonProps {
  children?: React.ReactNode;
  variant?: 'contained' | 'outlined' | 'text';
  onClick?: () => void;
  customColor?: string;
  textColor?: string;
}

const EliceButton = forwardRef<HTMLButtonElement, EliceButtonProps>(
  ({ children, variant = 'contained', color = 'primary', customColor, textColor: customTextColor, sx, ...rest }, ref) => {
    const theme = useTheme();

    // 커스텀 색상이 있으면 해당 색상 사용, 없으면 팔레트 색상 사용
    let colorMain: string;
    let colorHover: string;
    let colorDark: string;
    let textColor: string;

    if (customColor) {
      colorMain = customColor;
      // 호버 시 매우 미세한 색상 변화만 적용 (alpha 사용)
      colorHover = alpha(customColor, 0.9);
      colorDark = darken(customColor, 0.15);
      textColor = customTextColor || theme.palette.getContrastText(customColor);
    } else {
      const paletteColor = theme.palette[color as keyof typeof theme.palette] as { main: string; dark: string } | undefined;
      colorMain = paletteColor?.main || theme.palette.primary.main;
      // 팔레트 색상의 경우 hover용 색상을 alpha로 약간만 어둡게
      colorHover = alpha(colorMain, 0.9);
      colorDark = paletteColor?.dark || theme.palette.primary.dark;
      textColor = customTextColor || theme.palette.getContrastText(colorMain);
    }

    // sx prop에서 hover, focus, active, disabled 스타일 추출 (deep merge를 위해)
    const sxObj = typeof sx === 'function' ? sx(theme) : (Array.isArray(sx) ? sx.reduce((acc, item) => ({ ...acc, ...(typeof item === 'function' ? item(theme) : item) }), {}) : sx) || {};
    const sxHover = sxObj['&:hover'];
    const sxFocus = sxObj['&:focus'];
    const sxActive = sxObj['&:active'];
    const sxDisabled = sxObj['&:disabled'];

    // sx에서 hover, focus, active, disabled 제외한 나머지
    const { '&:hover': _, '&:focus': __, '&:active': ___, '&:disabled': ____, ...restSx } = sxObj;

    return (
      <Button
        ref={ref}
        variant={variant}
        color={customColor ? undefined : color}
        sx={[
          {
            borderRadius: '0.75rem',
            fontWeight: 600,
            transition: smoothColorTransform(theme),
            // Contained variant 스타일
            ...(variant === 'contained' && {
              backgroundColor: colorMain,
              color: textColor,
              border: `1px solid ${colorMain}`,

              '&:hover': {
                backgroundColor: colorHover,
                borderColor: colorHover,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${alpha(colorMain, 0.3)}`,
                // sx에서 전달된 hover 스타일 병합
                ...sxHover,
              },

              '&:focus': {
                backgroundColor: `${alpha(colorDark, 0.4)}`,
                borderColor: `${alpha(colorDark, 0.25)}`,
                outlineOffset: '2px',
                // sx에서 전달된 focus 스타일 병합
                ...sxFocus,
              },

              '&:active': {
                backgroundColor: colorDark,
                transform: 'translateY(0)',
                // sx에서 전달된 active 스타일 병합
                ...sxActive,
              },
            }),

            // Outlined variant 스타일
            ...(variant === 'outlined' && {
              backgroundColor: 'transparent',
              color: colorMain,
              border: `1px solid ${colorMain}`,

              '&:hover': {
                backgroundColor: alpha(colorMain, 0.08),
                borderColor: colorHover,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 8px ${alpha(colorMain, 0.12)}`,
                // sx에서 전달된 hover 스타일 병합
                ...sxHover,
              },

              '&:focus': {
                backgroundColor: alpha(colorMain, 0.2),
                borderColor: `${alpha(colorMain, 0.25)}`,
                outlineOffset: '2px',
                // sx에서 전달된 focus 스타일 병합
                ...sxFocus,
              },

              '&:active': {
                backgroundColor: alpha(colorMain, 0.3),
                transform: 'translateY(0)',
                // sx에서 전달된 active 스타일 병합
                ...sxActive,
              },
            }),

            // Text variant 스타일
            ...(variant === 'text' && {
              backgroundColor: 'transparent',
              color: colorMain,
              border: 'none',

              '&:hover': {
                backgroundColor: alpha(colorMain, 0.08),
                transform: 'translateY(-1px)',
                // sx에서 전달된 hover 스타일 병합
                ...sxHover,
              },

              '&:focus': {
                backgroundColor: alpha(colorMain, 0.2),
                outlineOffset: '2px',
                // sx에서 전달된 focus 스타일 병합
                ...sxFocus,
              },

              '&:active': {
                backgroundColor: alpha(colorMain, 0.3),
                transform: 'translateY(0)',
                // sx에서 전달된 active 스타일 병합
                ...sxActive,
              },
            }),

            // Disabled 상태 - variant별로 다르게 처리
            '&:disabled': {
              transform: 'none',
              boxShadow: 'none',
              ...(variant === 'contained' && {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
                border: 'none',
              }),
              ...(variant === 'outlined' && {
                backgroundColor: 'transparent',
                color: theme.palette.action.disabled,
                border: `1px solid ${theme.palette.action.disabledBackground}`,
              }),
              ...(variant === 'text' && {
                backgroundColor: 'transparent',
                color: theme.palette.action.disabled,
                border: 'none',
              }),
              // sx에서 전달된 disabled 스타일 병합
              ...sxDisabled,
            },

            // sx의 나머지 스타일 적용
            ...restSx,
          },
          // sx가 함수나 배열인 경우를 대비해 원본도 전달 (MUI가 자동으로 처리)
          ...(typeof sx === 'function' || Array.isArray(sx) ? [sx] : []),
        ]}
        {...rest}
      >
        <Typography variant="buttoninput">{children}</Typography>
      </Button>
    );
  }
);

export default EliceButton;
